import {
  BellOutlined,
  ClockCircleOutlined,
  ControlOutlined,
  MailOutlined,
  SendOutlined,
} from '@ant-design/icons'
import FormCard from '@components/FormCard'
import { SendTestModal } from '@components/notifications/push-wizard/components/SendTestModal'
import { NotificationSettingItem } from '@components/NotificationSettingList'
import { TextEditorVariable } from '@components/tiptap/tiptap-ui/variables-dropdown'
import { CommunicationKey } from '@models/Communication'
import { NotificationContentKey } from '@models/NotificationContent'
import { SettingKey } from '@models/Setting'
import { SBRMAction } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import MetadataKey from '@utils/MetadataKey'
import { Col } from 'antd'

export const bookingNotificationItems: (NotificationSettingItem & {
  slug: string
})[] = [
  {
    slug: 'reservation-placed',
    title: 'Reservation placed',
    onTestClick: (
      <SendTestModal
        communicationKey={CommunicationKey.EMAIL_RESERVATION_PLACED}
      />
    ),
    icon: <MailOutlined />,
    description: `Sent to the customer when they place a new reservation.
This email confirms that the reservation has been successfully placed. It reassures the customer that their request has been received.`,
    enabledSettingKey: SettingKey.NOTFICATION_RESERVATION_PLACED_ENABLED,
    content: (
      <>
        <Col span={14}>
          <FormCard
            title="Mail notification content"
            icon={<MailOutlined />}
            entity={Entity.notificationContent}
            action={SBRMAction.Update}
            metadata={[
              {
                key: MetadataKey.NotificationContent,
                value: NotificationContentKey.RESERVATION_PLACED,
              },
              {
                key: MetadataKey.textEditorVariables,
                value: [
                  TextEditorVariable.FULL_NAME,
                  TextEditorVariable.VENUE_NAME,
                ],
              },
            ]}
          />
        </Col>
        <Col span={10}>
          <img
            src="/assets/emailPreviews/placedReservationPreview.png"
            style={{ width: '100%' }}
          />
        </Col>
      </>
    ),
  },
  {
    slug: 'reservation-updated',
    title: 'Reservation updated',
    onTestClick: (
      <SendTestModal
        communicationKey={CommunicationKey.EMAIL_RESERVATION_UPDATED}
      />
    ),
    icon: <MailOutlined />,
    description: `Sent to the customer to confirm they made a change to their reservation (e.g. time, number of guests, or special requests).`,
    enabledSettingKey: SettingKey.NOTFICATION_RESERVATION_UPDATED_ENABLED,
    content: (
      <>
        <Col span={14}>
          <FormCard
            title="Mail notification content"
            icon={<MailOutlined />}
            entity={Entity.notificationContent}
            action={SBRMAction.Update}
            metadata={[
              {
                key: MetadataKey.NotificationContent,
                value: NotificationContentKey.RESERVATION_UPDATED,
              },
              {
                key: MetadataKey.textEditorVariables,
                value: [
                  TextEditorVariable.FULL_NAME,
                  TextEditorVariable.VENUE_NAME,
                ],
              },
            ]}
          />
        </Col>
        <Col span={10}>
          <img
            src="/assets/emailPreviews/placedReservationPreview.png"
            style={{ width: '100%' }}
          />
        </Col>
      </>
    ),
  },
  {
    slug: 'reservation-confirmation',
    title: 'Reservation confirmation',
    onTestClick: (
      <SendTestModal
        communicationKey={
          CommunicationKey.NOTIFICATION_RESERVATION_CONFIRMATION
        }
      />
    ),
    icon: <BellOutlined />,
    description: `Push notification sent before the reservation to ask the customer to confirm their attendance. This helps reduce no-shows and ensures the booking is still valid.`,
    enabledSettingKey: SettingKey.NOTFICATION_RESERVATION_CONFIRMATION_ENABLED,
    content: (
      <>
        <Col span={12}>
          <FormCard
            title="Confirmation status"
            description={`Reservation will be confirmed with this status`}
            icon={<ControlOutlined />}
            entity={Entity.setting}
            action={SBRMAction.Update}
            metadata={[
              {
                key: MetadataKey.Setting,
                value: SettingKey.MARKETING_CONFIRMATION_CONFIRMATION_STATUS,
              },
              {
                key: MetadataKey.ExtraProps,
                value: {
                  label: 'Status',
                },
              },
            ]}
          />
        </Col>
        <Col span={12}>
          <FormCard
            title="Time before reservation"
            description="When a reservation is placed within this delay, no notification will be scheduled. In case the reservation is canceled by restaurant staff, the notification will be canceled"
            icon={<ControlOutlined />}
            entity={Entity.setting}
            action={SBRMAction.Update}
            metadata={[
              {
                key: MetadataKey.Setting,
                value: SettingKey.MARKETING_CONFIRMATION_TIME_IN_ADVANCE,
              },
            ]}
          />
        </Col>
        <Col span={24}>
          <FormCard
            title="Push notification content"
            icon={<BellOutlined />}
            entity={Entity.notificationContent}
            action={SBRMAction.Update}
            metadata={[
              {
                key: MetadataKey.NotificationContent,
                value: NotificationContentKey.RESERVATION_CONFIRMATION,
              },
              {
                key: MetadataKey.textEditorVariables,
                value: [
                  TextEditorVariable.FULL_NAME,
                  TextEditorVariable.VENUE_NAME,
                ],
              },
            ]}
          />
        </Col>
      </>
    ),
  },
  {
    slug: 'reservation-cancelled',
    title: 'Reservation cancelled',
    onTestClick: (
      <SendTestModal
        communicationKey={CommunicationKey.EMAIL_RESERVATION_CANCELLED}
      />
    ),
    icon: <MailOutlined />,
    description: `Sent if the reservation is cancelled either by the venue or by the customer.
It ensures the customer is aware that their booking is no longer active and prevents confusion.`,
    enabledSettingKey: SettingKey.NOTFICATION_RESERVATION_CANCELLED_ENABLED,
    content: (
      <>
        <Col span={14}>
          <FormCard
            title="Mail notification content"
            icon={<MailOutlined />}
            entity={Entity.notificationContent}
            action={SBRMAction.Update}
            metadata={[
              {
                key: MetadataKey.NotificationContent,
                value: NotificationContentKey.RESERVATION_CANCELLED,
              },
              {
                key: MetadataKey.textEditorVariables,
                value: [
                  TextEditorVariable.FULL_NAME,
                  TextEditorVariable.VENUE_NAME,
                ],
              },
            ]}
          />
        </Col>
        <Col span={10}>
          <img
            src="/assets/emailPreviews/placedReservationPreview.png"
            style={{ width: '100%' }}
          />
        </Col>
      </>
    ),
  },
  {
    slug: 'reservation-no-show',
    title: 'Reservation no-show',
    onTestClick: (
      <SendTestModal
        communicationKey={CommunicationKey.NOTIFICATION_RESERVATION_NOSHOW}
      />
    ),
    icon: <BellOutlined />,
    description: `Sent when the customer does not show up at their reservation.
This optional notification can be used to inform the customer that their absence has been recorded.`,
    enabledSettingKey: SettingKey.NOTFICATION_RESERVATION_NO_SHOW_ENABLED,
    content: (
      <>
        <Col span={12}>
          <FormCard
            title="Status to trigger the push send"
            description="The no-show notification will be sent when the Reservation is set to this status"
            icon={<SendOutlined />}
            entity={Entity.setting}
            action={SBRMAction.Update}
            metadata={[
              {
                key: MetadataKey.Setting,
                value: SettingKey.MARKETING_NOSHOW_NOTIFICATION_STATUS,
              },
              {
                key: MetadataKey.ExtraProps,
                value: {
                  label: 'Status',
                },
              },
            ]}
          />
        </Col>
        <Col span={12}>
          <FormCard
            title="Notification delay"
            description="In case you want to delay the notification sending, you can define a custom delay here.
Set to 0 if you want to send immediately"
            icon={<ClockCircleOutlined />}
            entity={Entity.setting}
            action={SBRMAction.Update}
            metadata={[
              {
                key: MetadataKey.Setting,
                value: SettingKey.MARKETING_NOSHOW_NOTIFICATION_TIME,
              },
            ]}
          />
        </Col>
        <Col span={24}>
          <FormCard
            title="Push notification content"
            icon={<BellOutlined />}
            entity={Entity.notificationContent}
            action={SBRMAction.Update}
            metadata={[
              {
                key: MetadataKey.NotificationContent,
                value: NotificationContentKey.NO_SHOW,
              },
              {
                key: MetadataKey.textEditorVariables,
                value: [
                  TextEditorVariable.FULL_NAME,
                  TextEditorVariable.VENUE_NAME,
                ],
              },
            ]}
          />
        </Col>
      </>
    ),
  },
]
